/**
 * KEEPING THE TILL IN RECEIPT NUMBERS.
 *
 * `ReceiptNumbers` will not lease on its own. That is correct — leasing is an
 * ONLINE step and `issue()` runs at the exact moment a till must not depend on
 * a network, so the block has to be there already. Something has to put it
 * there, and for as long as this file did not exist NOTHING DID:
 * `ensureBlock()` and `topUpIfLow()` had zero callers in the composition root.
 *
 * The consequence was total and invisible. A till with working storage built a
 * `ReceiptNumbers` holding no block, `issue()` answered null, and `closeCheck`
 * refused every single check with `pos.error.noReceiptNumber`. Opening tables,
 * ringing and firing all worked — so the till LOOKED fine right up to the
 * moment somebody tried to take money.
 *
 * Every unit test passed, because they pass a block in. `e-unattended.spec.ts`
 * passed, because it calls `ensureBlock()` itself — a test that builds its own
 * dependency, one more time. It took generating a fortnight of trading through
 * the real classes to hit it, at check 158, when the hand-leased block ran out.
 *
 * So the policy lives here, as one tested function, and the composition root
 * calls it on the same cadence it syncs the menu.
 */

export interface NumberSource {
  /** Lease a first block if this period holds none at all. */
  ensureBlock(): Promise<void>;
  /** Lease another once the held blocks are near spent. */
  topUpIfLow(): Promise<void>;
  remaining(): number;
}

export interface NumberSyncResult {
  readonly held: number;
  /**
   * Translatable, and set only when the till is now at risk of refusing a
   * close. A failed top-up with 150 numbers left is not news.
   */
  readonly messageKey?: string;
}

/**
 * How few numbers left before the operator should be told.
 *
 * Not zero. At zero the next close has already failed, and the person holding
 * the card machine is the last one who should discover it.
 */
export const NUMBERS_LOW = 20;

export async function syncNumbers(source: NumberSource | undefined): Promise<NumberSyncResult> {
  // No durable storage means no lease store means no numbering, which
  // `sellState` already reports. Not an error, and not this function's news.
  if (!source) return { held: 0 };

  try {
    // Both, in this order, and neither is redundant. `ensureBlock` is the cold
    // start — nothing held at all. `topUpIfLow` is the running case, leasing
    // the next block while the current one still has a fifth left, so the
    // request happens during service rather than during a queue.
    await source.ensureBlock();
    await source.topUpIfLow();
  } catch (error) {
    const held = source.remaining();
    console.warn("numbering: could not reach the server —", (error as Error).message);
    // Offline with a healthy block is the DESIGNED state, not a degradation:
    // spending a leased block without a network is the entire point of leasing.
    return held > NUMBERS_LOW ? { held } : { held, messageKey: "pos.error.numbersLow" };
  }

  const held = source.remaining();
  return held > NUMBERS_LOW ? { held } : { held, messageKey: "pos.error.numbersLow" };
}
